<template>
  <a
    :key="article.id"
    :href="withTrailingSlash(`/articles/${article.slug}`)"
    class="group hover:shadow-[8px_8px_10px_0_rgba(219,170,49,0.10),28px_38px_60px_0_rgba(220,174,58,0.08)]] relative flex w-full flex-shrink-0 flex-col overflow-hidden rounded-lg bg-white shadow-[5px_5px_1px_0_rgba(219,170,49,0.10),10px_15px_25px_0_rgba(220,174,58,0.07)] transition-shadow duration-300 ease-out"
  >
    <!-- Image -->
    <div class="aspect-[3/2] overflow-hidden">
      <NuxtImg
        :src="image.sourceUrl"
        :alt="image.altText || article.title"
        class="h-full w-full rounded-[10px] border-4 border-white object-cover"
      />
    </div>

    <!-- Content -->
    <div class="flex grow flex-col items-start gap-3 p-8">
      <h3 class="text-brand-600 group-hover:text-brand-500 text-xl leading-[26px] font-black tracking-tight">
        {{ article.title }}
      </h3>

      <p v-if="article.date" class="text-[13px] leading-[165%] font-normal text-gray-700">
        <!-- {{ formatDate(article.date) }} -->
        <span v-if="article.areasOfStudy?.[0]">
          <!-- <span class="text-accent-500">&bull;</span> -->
          {{ article.areasOfStudy[0].name }}
        </span>
      </p>

      <div
        v-if="article.excerpt"
        class="excerpt mb-6 line-clamp-3 text-[13px] leading-[165%] text-gray-700"
        v-html="article.excerpt"
      />

      <BaseIconButton
        icon="arrow-right"
        icon-class="!size-[13px]"
        variant="light"
        aria-label="View Article"
        class="group-hover:bg-brand-50 mt-auto p-4"
      />
    </div>
  </a>
</template>

<script setup lang="ts">
import { withTrailingSlash } from '~/utils/urls';
interface ArticleImage {
  sourceUrl: string;
  altText?: string;
  srcSet?: string;
}

interface Article {
  id: string;
  title: string;
  slug: string;
  date?: string;
  excerpt?: string;

  featuredImage?: { node: ArticleImage | null };
  heroImage?: { node: ArticleImage | null };

  introduction?: {
    content?: string;
  };

  topics?: {
    id: string;
    name: string;
    slug: string;
  }[];

  areasOfStudy?: { id: string; name?: string; slug?: string }[];
}

interface TopicRow {
  heading: string;
  articles: Article[];
  topics: {
    id: string;
    name: string;
    slug: string;
  }[];
}

interface ArticleHub {
  heroHeading?: string;
  heroTeaser?: string;
  heroSearchPromptText?: string;
  featuredImage?: ArticleImage | null;
  featuredArticles?: Article[];
  topicRows?: TopicRow[];
}

interface ArticlesIndexResponse {
  hub: ArticleHub;
  articles: Article[];
}
const formatDate = (date?: string) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const fallBackImage: ArticleImage = {
  sourceUrl: '/images/article-placeholder.jpg',
  altText: 'Article image',
};

const getImage = (article: any): ArticleImage => {
  if (!article) return fallBackImage;
  const hero = article.heroImage?.sourceUrl ? article.heroImage : article.heroImage?.node;
  const feat = article.featuredImage?.sourceUrl ? article.featuredImage : article.featuredImage?.node;

  return hero || feat || fallBackImage;
};
const image = computed(() => getImage(props.article));

const props = defineProps<{ article: any }>();
</script>

<style scoped>
:deep(.excerpt p) {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
  color: inherit;
}
</style>
